import { normalizeHex } from "./harmonicPalette";

function groupsEqualFuzzy(a, b) {
  const ca = normalizeHex(a[0]) ?? String(a[0] ?? "").trim().toLowerCase();
  const cb = normalizeHex(b[0]) ?? String(b[0] ?? "").trim().toLowerCase();
  return ca === cb && a[1] === b[1];
}

function groupsEqualStrict(a, b) {
  return a[0] === b[0] && a[1] === b[1];
}

/** Same rule as the original Home.js detector: full array, period from index 0. */
export function isFullRepeatingFromStartStrict(groups, period) {
  const n = groups.length;
  for (let i = 0; i < n; i++) {
    if (!groupsEqualStrict(groups[i], groups[i % period])) return false;
  }
  return true;
}

export function smallestStrictPeriodLength(groups) {
  const n = groups.length;
  for (let size = 1; size <= n; size++) {
    if (isFullRepeatingFromStartStrict(groups, size)) return size;
  }
  return n;
}

function canonCell(cell) {
  return normalizeHex(cell) ?? String(cell ?? "").trim().toLowerCase();
}

/**
 * Smallest repeating unit of a flat cell sequence, using the same
 * period-from-index-0 rule as the run-group detector: a period may be followed
 * by a partial tail. Returns the full length when nothing shorter repeats.
 */
export function smallestCellPeriodLength(cells) {
  const n = cells.length;
  if (n === 0) return 0;

  const canon = cells.map(canonCell);

  for (let size = 1; size <= n; size++) {
    let repeats = true;
    for (let i = size; i < n; i++) {
      if (canon[i] !== canon[i % size]) {
        repeats = false;
        break;
      }
    }
    if (repeats) return size;
  }

  return n;
}

/**
 * Cell range of one repeating unit in a stringing-order sequence.
 * Empty cells are skipped for detection (same as the grouped recipe) but stay
 * inside the range so they can be drawn as placeholders.
 *
 * @param {unknown[]} cells
 * @param {(cell: unknown) => boolean} isEmpty
 * @returns {{ start: number, end: number }}
 */
export function oneRepeatCellRange(cells, isEmpty) {
  const n = Array.isArray(cells) ? cells.length : 0;
  if (n === 0) return { start: 0, end: 0 };

  const groups = [];
  for (let i = 0; i < n; i++) {
    if (isEmpty(cells[i])) continue;
    const color = canonCell(cells[i]);
    const last = groups[groups.length - 1];
    if (last && last.color === color) {
      last.count += 1;
      last.end = i + 1;
    } else {
      groups.push({ color, count: 1, start: i, end: i + 1 });
    }
  }

  if (groups.length === 0) return { start: 0, end: 0 };

  const pairs = groups.map((g) => [g.color, g.count]);
  const strictSize = smallestStrictPeriodLength(pairs);

  let from = 0;
  let size = groups.length;
  if (strictSize < groups.length) {
    size = strictSize;
  } else {
    const trimmed = findTrimmedRunLengthRepeat(pairs);
    if (trimmed) {
      from = trimmed.trimStart;
      size = trimmed.templateGroups.length;
    }
  }

  return {
    start: groups[from].start,
    end: groups[from + size - 1].end,
  };
}

/**
 * When strict detection only finds the trivial whole-array period, find a
 * shorter unit by allowing a trimmed prefix, an optional matching partial tail,
 * and trailing non-matching groups (noise).
 */
function evaluateTrimmedRepeat(groups, start, period, minFullCycles) {
  const n = groups.length;
  if (start + period > n) return null;

  const template = groups.slice(start, start + period);
  let pos = start;
  let fullCycles = 0;

  while (pos + period <= n) {
    let ok = true;
    for (let j = 0; j < period; j++) {
      if (!groupsEqualFuzzy(groups[pos + j], template[j])) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    fullCycles++;
    pos += period;
  }

  if (fullCycles < minFullCycles) return null;

  let tail = 0;
  const rem = n - pos;
  if (rem > 0) {
    let tailOk = true;
    for (let j = 0; j < rem; j++) {
      if (!groupsEqualFuzzy(groups[pos + j], template[j])) {
        tailOk = false;
        break;
      }
    }
    if (tailOk) tail = rem;
  }

  const covered = fullCycles * period + tail;
  return {
    trimStart: start,
    period,
    templateGroups: template,
    fullCycles,
    tailGroups: tail,
    covered,
  };
}

/**
 * @param {Array<[string, number]>} groups
 * @returns { { templateGroups: Array<[string, number]>, trimStart: number } | null }
 */
export function findTrimmedRunLengthRepeat(groups, options = {}) {
  const maxStartTrim = options.maxStartTrim ?? 24;
  const maxPeriod = options.maxPeriod ?? 48;
  const minFullCycles = options.minFullCycles ?? 2;
  const minCoverageRatio = options.minCoverageRatio ?? 0.42;

  const n = groups.length;
  if (n < 2) return null;

  let best = null;
  let bestKey = null;

  const better = (key) => {
    if (!bestKey) return true;
    for (let i = 0; i < key.length; i++) {
      if (key[i] !== bestKey[i]) return key[i] > bestKey[i];
    }
    return false;
  };

  for (let start = 0; start <= Math.min(maxStartTrim, n - 1); start++) {
    const maxP = Math.min(maxPeriod, n - start);
    for (let p = 1; p <= maxP; p++) {
      if (start + p * minFullCycles > n) continue;
      const ev = evaluateTrimmedRepeat(groups, start, p, minFullCycles);
      if (!ev) continue;
      const coverage = ev.covered / n;
      if (coverage < minCoverageRatio) continue;
      if (ev.period >= n) continue;

      const key = [ev.covered, -ev.period, ev.fullCycles, -start];
      if (better(key)) {
        bestKey = key;
        best = ev;
      }
    }
  }

  if (!best) return null;
  return {
    templateGroups: best.templateGroups,
    trimStart: best.trimStart,
  };
}
